import TileWrapper from '../TileWrapper'
import { Link } from 'react-router-dom'
import { useTeam } from 'app/frontend/base/config/context/team'

import { useOverviewData } from 'app/frontend/base/config/queries'
import UpcomingEvent, {
  useUpcomingEventDetails,
} from 'app/frontend/base/components/UpcomingEvent'
import { useTile } from '../../context'
import { cn } from '@/lib/utils'
import EventAvatar from 'app/frontend/base/components/EventAvatar'
import dayjs from 'dayjs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CharacterClassIcon } from 'app/frontend/base/components'
import EventStatusIcon from 'app/frontend/base/components/EventStatusIcon'
import LinkButton from '@/components/ui/link-button'

const UpcomingEventsTile = () => {
  const { tile } = useTile()
  const { team } = useTeam()
  const { data: overviewData } = useOverviewData(team.id)
  const events = overviewData?.upcomingEvents || []

  if (events.length === 0) {
    return (
      <TileWrapper>
        <p className='text-muted-foreground'>No upcoming events scheduled.</p>
      </TileWrapper>
    )
  }

  const amount = Math.abs(tile.config?.amount ?? 5)

  return (
    <TileWrapper
      cta={
        <LinkButton to={`${team.path}/events`} size='xs'>
          View all
        </LinkButton>
      }
    >
      <ul className='space-y-2'>
        {events.slice(0, amount).map((event) => (
          <li
            key={event.id}
            className={cn(
              'border-t first:border-t-0 pt-2',
              tile.config?.title ? '' : 'first:pt-0',
            )}
          >
            <Link to={`${team.path}/events/${event.id}`} className='group'>
              {tile.position === 'main' ? (
                <DetailedUpcomingEvent eventReference={event} />
              ) : null}

              <UpcomingEvent
                className={tile.position === 'main' ? 'sm:hidden' : ''}
                eventReference={event}
              />
            </Link>
          </li>
        ))}
      </ul>
    </TileWrapper>
  )
}

const DetailedUpcomingEvent = ({
  eventReference,
}: {
  eventReference: wowaudit.EventReference
}) => {
  const {
    characterReference,
    amountPresent,
    participants,
    hasSufficientParticipants,
  } = useUpcomingEventDetails(eventReference)

  return (
    <div className='hidden sm:block'>
      <div className='flex items-center justify-between gap-4 w-full'>
        <div className='flex items-center gap-2'>
          <EventAvatar eventReference={eventReference} className='h-10 w-10' />
          <div>
            <header className='capitalize group-hover:text-primary transition-colors'>
              {eventReference.title ??
                eventReference.primaryActivity.instance?.name ??
                eventReference.primaryActivity.activityType}
            </header>

            <div className={'capitalize text-xs text-muted-foreground'}>
              {eventReference.primaryActivity.difficulty.replace('_', ' ')}
            </div>
          </div>
        </div>

        <Tooltip>
          <TooltipTrigger>
            <div>
              {dayjs(eventReference.startsAt).isBefore(dayjs())
                ? 'started '
                : ''}
              {dayjs(eventReference.startsAt).fromNow()}
            </div>

            <div className='text-xs text-muted-foreground'>
              {dayjs(eventReference.startsAt).format('HH:mm')} -{' '}
              {dayjs(eventReference.endsAt).format('HH:mm')}
            </div>
          </TooltipTrigger>
          <TooltipContent>
            {dayjs(eventReference.startsAt).format('dddd, D MMM YYYY')}
          </TooltipContent>
        </Tooltip>

        {characterReference ? (
          <div className='flex items-center gap-3 py-1 px-4 bg-background rounded-xl'>
            <CharacterClassIcon
              size='medium'
              characterClass={characterReference.characterClass}
            />
            <span>{characterReference.name}</span>
            <EventStatusIcon
              status={participants[characterReference.id].status}
            />
          </div>
        ) : null}

        <span className={`flex text-base gap-1`}>
          <span
            className={cn(
              hasSufficientParticipants
                ? 'text-success-foreground'
                : 'text-warning-foreground',
            )}
          >
            {amountPresent}
          </span>
          <span>/</span>
          <span>{eventReference.participantReferences.length}</span>
        </span>
      </div>
    </div>
  )
}

export default UpcomingEventsTile
